import { useEffect, useState } from "react";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import AdminDataModal from "../../components/AdminDataModal";
import type { FormField } from "../../components/AdminDataModal";
import { useMessageModal } from "../../contexts/MessageModalContext";
import { useFormat } from "../../../context/FormatContext";

interface WelcomeBanner {
  id: number;
  title: string;
  image: string;
  rendering_page_link: string;
  link: string;
  start_date: string;
  end_date: string;
  priority: number;
  status: string;
}

export default function WelcomeBanners() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const IMAGE_URL = import.meta.env.VITE_LYRAZONE_IMAGE_URL;

  const { showModal } = useMessageModal();
  const { formatDateOnly } = useFormat();

  const [data, setData] = useState<WelcomeBanner[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const formatDateForInput = (value: any) => {
    if (!value) return "";

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const welcomeBannerFields: FormField[] = [
    { key: "id", label: "ID", colSpan: 2 },

    { key: "title", label: "Title", colSpan: 2 },

    {
      key: "image",
      label: "Welcome Banner",
      type: "image",
      imageType: "single",
      imageFolder: "welcomeBanner",
      uploadField: "welcomeBanner",
      colSpan: 2,
    },

    {
      key: "rendering_page_link",
      label: "Rendering Page Link",
      colSpan: 2,
    },

    {
      key: "link",
      label: "OnClick Link",
      colSpan: 2,
    },

    {
      key: "start_date",
      label: "Start Date",
      type: "date",
      colSpan: 2,
    },

    {
      key: "end_date",
      label: "End Date",
      type: "date",
      colSpan: 2,
    },

    {
      key: "priority",
      label: "Priority",
      type: "number",
      colSpan: 2,
    },

    {
      key: "status",
      label: "Status",
      type: "select",
      options: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
      ],
      colSpan: 2,
    },
  ];

  const columns = [
    { key: "title", label: "Title" },
    { key: "image", label: "Image", imageFolder: "promotions" },
    { key: "rendering_page_link", label: "Rendering Page Link" },
    { key: "link", label: "OnClick Link" },
    {
      key: "start_date",
      label: "Start Date",
      type: "date",
      colSpan: 2,
    },
    {
      key: "end_date",
      label: "End Date",
      type: "date",
      colSpan: 2,
    },
    { key: "priority", label: "Priority" },
    {
      key: "status",
      label: "Status",
      render: (row: any) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status?.charAt(0).toUpperCase() + row.status?.slice(1)}
        </span>
      ),
    },
  ];

  const fetchWelcomeBanners = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/welcome-banners`, {
        credentials: "include",
      });

      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);

        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Welcome Banners Error:", error);
    }
  };

  useEffect(() => {
    fetchWelcomeBanners();
  }, []);

  const buildFormData = (
    formData: any,
    fields: FormField[],
    activeRow?: any,
  ) => {
    const form = new FormData();

    fields.forEach((field) => {
      const value = formData[field.key];

      if (field.type === "image" && field.imageType === "product") {
        if (Array.isArray(value)) {
          value.forEach((img: any, index: number) => {
            if (img?.file) {
              form.append(field.uploadField || "image", img.file);
            }

            if (!img && activeRow?.images?.[index]?.id) {
              form.append("removeImages[]", activeRow.images[index].id);
            }
          });
        }
      } else if (field.type === "image") {
        if (value?.file) {
          form.append(field.uploadField || field.key, value.file);
        } else if (value?.removed) {
          form.append(field.uploadField || field.key, "REMOVE");
        }
      } else {
        form.append(field.key, value ?? "");
      }
    });

    return form;
  };

  const handleSubmit = async (formData: any) => {
    try {
      const form = buildFormData(formData, welcomeBannerFields, activeRow);

      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/welcome-banners`
          : `${BASE_URL}/admin/welcome-banners/${activeRow.id}`;

      const res = await fetch(url, {
        method,
        credentials: "include",
        body: form,
      });

      const result = await res.json();

      if (result.success) {
        fetchWelcomeBanners();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Saved",
          message: "Welcome banner saved successfully.",
          confirmText: "OK",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Failed to save welcome banner.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      console.error("Submit Welcome Banner Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Something went wrong while saving welcome banner.",
        confirmText: "OK",
      });
    }
  };

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Welcome Banner",
      message: `Are you sure you want to delete "${row.title}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/admin/welcome-banners/${row.id}`,
            {
              method: "DELETE",
              credentials: "include",
            },
          );

          const result = await res.json();

          if (result.success) {
            fetchWelcomeBanners();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Welcome banner deleted successfully.",
              confirmText: "OK",
            });
          } else {
            showModal({
              type: "error",
              title: "Error",
              message: result.message || "Failed to delete welcome banner.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Welcome Banner Error:", error);

          showModal({
            type: "error",
            title: "Error",
            message: "Something went wrong while deleting welcome banner.",
            confirmText: "OK",
          });
        }
      },
    });
  };

  return (
    <div>
      <AdminListToolkit
        title="Welcome Banners"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow(null);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");

          const formattedRow = {
            ...row,
            start_date: formatDateForInput(row.start_date),
            end_date: formatDateForInput(row.end_date),
          };

          setActiveRow(formattedRow);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");

          const formattedRow = {
            ...row,
            start_date: formatDateForInput(row.start_date),
            end_date: formatDateForInput(row.end_date),
          };

          setActiveRow(formattedRow);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={false}
      >
        {(filteredData) => (
          <div className="algorithm-grid">
            {filteredData.map((item: WelcomeBanner) => (
              <div
                key={item.id}
                className={`algorithm-card ${
                  selectedRow?.id === item.id ? "selected" : ""
                }`}
                onClick={() => setSelectedRow(item)}
              >
                <h3>{item.title}</h3>

                {item.image && (
                  <img
                    src={`${IMAGE_URL}/uploads/welcomeBanner/${item.image}`}
                    alt={item.title}
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      marginBottom: "10px",
                    }}
                  />
                )}

                <p>
                  <strong>Rendering Page:</strong>{" "}
                  {item.rendering_page_link || "-"}
                </p>

                <p>
                  <strong>OnClick Link:</strong> {item.link || "-"}
                </p>

                <p>Start : {formatDateOnly(item.start_date)}</p>

                <p>End : {formatDateOnly(item.end_date)}</p>

                <p>
                  <strong>Priority:</strong> {item.priority}
                </p>

                <p>
                  <strong>Status:</strong>{" "}
                  <span
                    className={
                      item.status === "active"
                        ? "badge-success"
                        : "badge-danger"
                    }
                  >
                    {item.status?.charAt(0).toUpperCase() +
                      item.status?.slice(1)}
                  </span>
                </p>
              </div>
            ))}
          </div>
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Welcome Banner"
        fields={welcomeBannerFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
